<template>
  <div class="flex justify-center sm:w-100 mx-auto transition-all duration-300">
    <form ref="formRef" action="/login/index.php" method="POST" @submit.prevent="executeLoginFlow" data-cy="auth-login-form"
          class="flex flex-col gap-4 pt-6 w-full">

      <UAlert v-if="loginError" :icon="loginError.icon" :color="loginError.color" variant="subtle" data-cy="auth-login-alert"
              :title="loginError.titulo" :description="loginError.descricao" class="mb-2" />

      <UFormField label="Email" name="username" size="lg">
        <UInput name="username" v-model="username" placeholder="seuemail@ifmg.edu.br" data-cy="login-email-input"
                class="w-full" size="lg" required />
      </UFormField>

      <UFormField label="Senha" name="password" size="lg">
        <UInput name="password" v-model="password" :type="showLoginPassword ? 'text' : 'password'" data-cy="login-password-input"
                placeholder="••••••••" class="w-full" size="lg" required>
          <template #trailing>
            <UButton color="neutral" variant="link" type="button" :icon="showLoginPassword ? 'ph:eye-slash' : 'ph:eye'"
                     :padded="false" @click="showLoginPassword = !showLoginPassword" />
          </template>
        </UInput>
      </UFormField>

      <div class="flex justify-between items-center text-sm text-gray-600">
        <label class="flex items-center gap-2">
          <input type="checkbox" name="rememberusername" value="1" class="w-4 h-4"/> Lembrar de mim
        </label>
        <UButton variant="link" class="cursor-pointer underline"
                 @click="uiStore.openForgotModal(); uiStore.closeAuthModal()">Esqueceu a senha?</UButton>
      </div>

      <AppButtonPrimary type="submit" class="justify-center mt-4" data-cy="login-submit-button" :loading="userStore.isLogging"> Entrar </AppButtonPrimary>

    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { useRoute } from 'vue-router'

defineOptions({
  name: 'AuthLogin'
})

const toast = useToast()
const route = useRoute()
const userStore = useUserStore()
const uiStore = useUiStore()

const showLoginPassword = ref(false)
const username = ref('')
const password = ref('')

// Interface para padronizar a estrutura visual e textual dos erros
interface LoginErrorDef {
  titulo: string;
  descricao: string;
  color: 'error' | 'warning' | 'info' | 'secondary';
  icon: string;
}

// Estado para armazenar o erro atual exibido no UAlert
const loginError = ref<LoginErrorDef | null>(null)

// Dicionário ÚNICO e centralizado de erros (URL e API)
const moodleErrors: Record<string, LoginErrorDef> = {
  // Erros de Redirecionamento da URL (Nativos do Moodle)
  '1': { titulo: 'Cookies Desabilitados', descricao: 'Para entrar no sistema, você precisa habilitar os cookies no seu navegador.', color: 'warning', icon: 'ph:cookie-bold' },
  '2': { titulo: 'Usuário Inválido', descricao: 'O formato do nome de usuário digitado não é permitido.', color: 'error', icon: 'ph:warning-circle-bold' },
  '3': { titulo: 'Acesso Negado', descricao: 'E-mail ou senha incorretos. Por favor, tente novamente.', color: 'error', icon: 'ph:warning-circle-bold' },
  '4': { titulo: 'Sessão Vencida', descricao: 'Sua autenticação foi encerrada por tempo de inatividade. Entre novamente.', color: 'warning', icon: 'ph:clock-bold' },

  // Erros da API de Token (Retorno JSON)
  'invalidlogin': { titulo: 'Acesso Negado', descricao: 'O nome de usuário ou senha estão incorretos, ou a conta está inativa.', color: 'error', icon: 'ph:warning-circle-bold' },
  'cannotcreatetoken': { titulo: 'Criação de Token Bloqueada', descricao: 'A criação de um token de API automatizada está desabilitada.', color: 'warning', icon: 'ph:shield-warning-bold' },
  'forcepasswordchange': { titulo: 'Ação Necessária', descricao: 'Sua conta exige uma atualização de dados. Acesse pelo portal web para regularizar.', color: 'info', icon: 'ph:password-bold' },
  'usernotfullysetup': { titulo: 'Ação Necessária', descricao: 'Sua conta exige uma atualização de dados. Acesse pelo portal web para regularizar.', color: 'secondary', icon: 'ph:user-gear-bold' },
  'network_error': { titulo: 'Erro de Conexão', descricao: 'Não foi possível conectar ao servidor Moodle. Verifique sua conexão e tente novamente.', color: 'error', icon: 'ph:wifi-slash-bold' },
  'servicenotavailable': { titulo: 'Serviço Indisponível', descricao: 'O serviço de autenticação externa não está habilitado no servidor.', color: 'error', icon: 'ph:plugs-connected-x-bold' },

  // Fallback (Erro genérico não mapeado)
  'default': { titulo: 'Falha na Autenticação', descricao: 'Ocorreu um erro inesperado ao processar seu login.', color: 'error', icon: 'ph:warning-circle-bold' }
}

watch(
  () => route.query.errorcode,
  (newCode) => {
    const code = newCode as string
    if (code && moodleErrors[code]) {
      loginError.value = moodleErrors[code]
    } else {
      loginError.value = null
    }
  },
  { immediate: true }
)

const formRef = ref<HTMLFormElement | null>(null)

const executeLoginFlow = async () => {
  loginError.value = null;

  // Ativa o loading do botão manualmente se quiser manter o feedback visual
  userStore.isLogging = true;

  const result = await userStore.handleFormSubmit(username.value, password.value)

  // SUCESSO ou BYPASS (Admin)
  if (result.success || result.errorcode === 'cannotcreatetoken' || result.errorcode === 'servicenotavailable') {

    if (result.errorcode === 'cannotcreatetoken' || result.errorcode === 'servicenotavailable') {
      toast.add({
        title: 'Criação de token bypassou',
        description: 'Redirecionando para a finalização do login nativo...',
        icon: 'ph:shield-check-bold',
        color: 'secondary', // Mantém a cor secundária exclusiva do fluxo de sucesso do Admin
        duration: 4000
      })

      setTimeout(() => {
        if (formRef.value) {
          (formRef.value as HTMLFormElement).submit();
          uiStore.closeAuthModal();
        }
      }, 4000)

    } else {
      // Usuário comum
      if (formRef.value) {
        (formRef.value as HTMLFormElement).submit();
        uiStore.closeAuthModal();
      }
    }

  } else {
    // FALHA: Captura o erro mapeado ou o fallback genérico
    const errorCode = result.errorcode || 'default';
    const errorDef = moodleErrors[errorCode] || moodleErrors['default'] as LoginErrorDef;

    // Se for o default e o servidor devolveu uma mensagem específica, injeta a mensagem bruta
    const finalDescription = (errorCode === 'default' && result.message)
        ? result.message
        : errorDef.descricao;

    toast.add({
      title: errorDef.titulo,
      description: finalDescription,
      icon: errorDef.icon,
      color: errorDef.color
    });
  }
}

</script>